import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { APP_NAME } from '../config/branding';

interface StateCount {
  state: string;
  stateName: string;
  count: number;
  cities: string[];
}

const STATE_NAMES: { [key: string]: string } = {
  'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
  'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
  'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
  'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
  'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
  'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
  'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
  'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
  'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
  'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming'
};

const PracticesDirectory: React.FC = () => {
  usePageTitle('Veterinary Practices Directory');
  
  const [stateCounts, setStateCounts] = useState<StateCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalPractices, setTotalPractices] = useState(0);

  useEffect(() => {
    const fetchPractices = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/practices/`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch practices');
        }

        const practices = await response.json();
        
        // Group practices by state
        const stateMap = new Map<string, { count: number; cities: Set<string> }>();
        
        practices.forEach((practice: any) => {
          if (practice.state && practice.is_active) {
            if (!stateMap.has(practice.state)) {
              stateMap.set(practice.state, { count: 0, cities: new Set() });
            }
            const stateData = stateMap.get(practice.state)!;
            stateData.count++;
            if (practice.city) {
              stateData.cities.add(practice.city);
            }
          }
        });

        // Convert to array and sort by count (descending)
        const counts: StateCount[] = Array.from(stateMap.entries())
          .map(([state, data]) => ({
            state,
            stateName: STATE_NAMES[state.toUpperCase()] || state,
            count: data.count,
            cities: Array.from(data.cities).sort()
          }))
          .sort((a, b) => b.count - a.count);

        setStateCounts(counts);
        setTotalPractices(practices.filter((p: any) => p.is_active).length);
      } catch (err) {
        console.error('Error fetching practices:', err);
        setError('Failed to load practice directory');
      } finally {
        setLoading(false);
      }
    };

    fetchPractices();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading practice directory...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Oops!</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            The {APP_NAME} Network
          </h1>
          <p className="text-xl text-gray-600 mb-2">
            Veterinary practices powered by {APP_NAME} across the United States
          </p>
          <p className="text-lg text-gray-500">
            {totalPractices.toLocaleString()} practices • {stateCounts.length} states
          </p>
        </div>

        {/* States Grid */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Browse by State</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {stateCounts.map((stateData) => (
              <Link
                key={stateData.state}
                to={`/practice/${stateData.state}`}
                className="group block p-6 bg-gray-50 hover:bg-blue-50 rounded-lg transition-all hover:shadow-md"
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600">
                    {stateData.stateName}
                  </h3>
                  <span className="text-sm font-medium text-gray-500 group-hover:text-blue-600">
                    {stateData.state}
                  </span>
                </div>
                <p className="text-sm text-gray-600">
                  {stateData.count} {stateData.count === 1 ? 'practice' : 'practices'}
                </p>
                {stateData.cities.length > 0 && (
                  <p className="text-xs text-gray-500 mt-2">
                    {stateData.cities.slice(0, 3).join(', ')}
                    {stateData.cities.length > 3 && ` +${stateData.cities.length - 3} more`}
                  </p>
                )}
              </Link>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default PracticesDirectory;

